import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Battery, PenTool as Tool, Wallet, GraduationCap, ArrowRight, Shield, Zap, Clock, FileCheck, Users, Building2, Phone, MessageSquare, Calendar, MapPin, Star, ChevronRight
} from 'lucide-react';

const Services: React.FC = () => {
  const mainServices = [
    {
      id: 'stations',
      icon: <Battery className="w-12 h-12 text-primary-500" />,
      title: 'Stations de Recharge',
      description: 'Un réseau de stations Proxy Power pour une recharge rapide et fiable de votre véhicule électrique.',
      image: 'https://images.pexels.com/photos/3819830/pexels-photo-3819830.jpeg',
      features: [
        'Recharge rapide en 2-3 heures',
        'Réseau disponible 24/7',
        'Paiement sans contact',
        'Sites sécurisés',
        'Support technique',
        'Géolocalisation'
      ],
      cta: {
        primary: {
          label: 'Trouver une station',
          link: '/stations'
        },
        secondary: {
          label: 'En savoir plus',
          link: '/proxy-power'
        }
      }
    },
    {
      id: 'maintenance',
      icon: <Tool className="w-12 h-12 text-primary-500" />,
      title: 'Maintenance & SAV',
      description: 'Service après-vente professionnel et maintenance préventive pour garder votre moto en parfait état.',
      image: 'https://images.pexels.com/photos/8993561/pexels-photo-8993561.jpeg',
      features: [
        'Entretien préventif régulier',
        'Réparations rapides',
        'Pièces d\'origine garanties',
        'Techniciens certifiés',
        'Suivi numérique',
        'Garantie interventions'
      ],
      cta: {
        primary: {
          label: 'Prendre RDV',
          link: '/contact'
        }
      }
    },
    {
      id: 'financing',
      icon: <Wallet className="w-12 h-12 text-primary-500" />,
      title: 'Leasing & Financement',
      description: 'Solutions de financement flexibles adaptées à vos besoins pour acquérir votre moto électrique.',
      image: 'https://images.pexels.com/photos/7681091/pexels-photo-7681091.jpeg',
      features: [
        'Pay-as-you-go personnalisé',
        'Leasing professionnel',
        'Assurance incluse',
        'Maintenance comprise',
        'Options de rachat',
        'Flexibilité des mensualités'
      ],
      cta: {
        primary: {
          label: 'Simuler un financement',
          link: '/pay-as-you-go'
        }
      }
    },
    {
      id: 'academy',
      icon: <GraduationCap className="w-12 h-12 text-primary-500" />,
      title: 'Proxy Academy',
      description: 'Formation complète pour maîtriser votre moto électrique et optimiser son utilisation.',
      image: 'https://images.pexels.com/photos/8964915/pexels-photo-8964915.jpeg',
      features: [
        'Formation à la conduite',
        'Conseils d\'utilisation',
        'Sécurité routière',
        'Maintenance de base',
        'Certification officielle',
        'Support continu'
      ],
      cta: {
        primary: {
          label: 'Découvrir les Formations',
          link: '/training'
        }
      }
    }
  ];

  const benefits = [
    {
      icon: <Shield className="w-8 h-8 text-accent-300" />,
      title: "Qualité Garantie",
      description: "Des services certifiés répondant aux plus hauts standards de qualité"
    },
    {
      icon: <Zap className="w-8 h-8 text-accent-300" />,
      title: "Performance Optimale",
      description: "Maximisez les performances de votre véhicule électrique"
    },
    {
      icon: <Clock className="w-8 h-8 text-accent-300" />,
      title: "Disponibilité 24/7",
      description: "Une assistance technique disponible à tout moment"
    },
    {
      icon: <FileCheck className="w-8 h-8 text-accent-300" />,
      title: "Suivi Personnalisé",
      description: "Un accompagnement sur mesure pour votre satisfaction"
    }
  ];

  const stats = [
    {
      icon: <Users className="w-8 h-8 text-primary-600" />,
      value: "5000+",
      label: "Clients Satisfaits"
    },
    {
      icon: <Tool className="w-8 h-8 text-primary-600" />,
      value: "15000+",
      label: "Interventions"
    },
    {
      icon: <Building2 className="w-8 h-8 text-primary-600" />,
      value: "50+",
      label: "Points Service"
    },
    {
      icon: <Star className="w-8 h-8 text-primary-600" />,
      value: "98%",
      label: "Satisfaction"
    }
  ];

  const contactInfo = [
    {
      icon: <Phone size={24} className="text-primary-600" />,
      title: "Service Client",
      content: "+221 77 123 45 67",
      action: "Appeler"
    },
    {
      icon: <MessageSquare size={24} className="text-primary-600" />,
      title: "Support Technique",
      content: "support@proxy-emobility.com",
      action: "Envoyer un email"
    },
    {
      icon: <Calendar size={24} className="text-primary-600" />,
      title: "Horaires",
      content: "Lun-Ven: 9h-18h",
      action: "Prendre RDV"
    },
    {
      icon: <MapPin size={24} className="text-primary-600" />,
      title: "Showroom",
      content: "123 Avenue de l'Indépendance, Dakar",
      action: "Voir sur la carte"
    }
  ];

  return (
    <div className="pt-20">
      <Helmet>
        <title>Services | Proxy Emobility - Maintenance, Financement et Formation</title>
        <meta name="description" content="Découvrez nos services complets : stations de recharge, maintenance et SAV, solutions de financement flexibles et formations pour maîtriser votre moto électrique." />
        <meta name="keywords" content="services mobilité électrique, maintenance motos électriques, financement, leasing, pay-as-you-go, formation, stations de recharge, Proxy Power, SAV" />
        <link rel="canonical" href="https://proxyemobility.netlify.app/services" />
        
        {/* Schema.org structured data for Service */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "ItemList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "item": {
                    "@type": "Service",
                    "name": "Stations de Recharge",
                    "description": "Un réseau de stations Proxy Power pour une recharge rapide et fiable de votre véhicule électrique.",
                    "provider": {
                      "@type": "Organization",
                      "name": "Proxy Emobility"
                    },
                    "serviceType": "Recharge",
                    "url": "https://proxyemobility.netlify.app/stations"
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "item": {
                    "@type": "Service",
                    "name": "Maintenance & SAV",
                    "description": "Service après-vente professionnel et maintenance préventive pour garder votre moto en parfait état.",
                    "provider": {
                      "@type": "Organization",
                      "name": "Proxy Emobility"
                    },
                    "serviceType": "Maintenance",
                    "url": "https://proxyemobility.netlify.app/services#maintenance"
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "item": {
                    "@type": "Service",
                    "name": "Leasing & Financement",
                    "description": "Solutions de financement flexibles adaptées à vos besoins pour acquérir votre moto électrique.",
                    "provider": {
                      "@type": "Organization",
                      "name": "Proxy Emobility"
                    },
                    "serviceType": "Financement",
                    "url": "https://proxyemobility.netlify.app/pay-as-you-go"
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 4,
                  "item": {
                    "@type": "Service",
                    "name": "Proxy Academy",
                    "description": "Formation complète pour maîtriser votre moto électrique et optimiser son utilisation.",
                    "provider": {
                      "@type": "Organization",
                      "name": "Proxy Emobility"
                    },
                    "serviceType": "Formation",
                    "url": "https://proxyemobility.netlify.app/training"
                  }
                }
              ]
            }
          `}
        </script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-primary-900 text-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,_rgba(255,255,255,0.1)_0%,_transparent_70%)]"></div>
        <div className="container mx-auto px-4 md:px-6 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Nos Services
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Une gamme complète de services pour vous accompagner dans votre transition vers la mobilité électrique. De la maintenance à la formation, nous sommes là pour vous.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-4">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="space-y-24">
            {mainServices.map((service, index) => (
              <div 
                key={service.id}
                id={service.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-primary-50 rounded-lg">
                      {service.icon}
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900">
                      {service.title}
                    </h2>
                  </div>
                  
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-all">
                        <FileCheck size={20} className="text-primary-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex flex-wrap gap-4">
                    <Link 
                      to={service.cta.primary.link}
                      className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-md hover:shadow-lg inline-flex items-center"
                    >
                      {service.cta.primary.label}
                      <ArrowRight size={20} className="ml-2" />
                    </Link>
                    {service.cta.secondary && (
                      <Link 
                        to={service.cta.secondary.link}
                        className="px-6 py-3 bg-white text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                      >
                        {service.cta.secondary.label}
                      </Link>
                    )}
                  </div>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden shadow-xl">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nos Engagements
            </h2>
            <p className="text-lg text-gray-600">
              Des services d'excellence pour une expérience client optimale
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg border border-primary-100 hover:border-primary-200 transition-all hover:shadow-xl group">
                <div className="mb-4 transform group-hover:scale-110 transition-transform">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nous Contacter
            </h2>
            <p className="text-lg text-gray-600">
              Notre équipe est à votre disposition pour répondre à toutes vos questions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <Link
                key={index}
                to="/contact"
                className="bg-white p-6 rounded-xl shadow-lg border border-primary-100 hover:border-primary-200 transition-all hover:shadow-xl group"
              >
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-primary-50 rounded-lg mr-3">
                    {info.icon}
                  </div>
                  <h3 className="font-semibold text-gray-900">{info.title}</h3>
                </div>
                <p className="text-gray-600 mb-4">{info.content}</p>
                <span className="inline-flex items-center text-primary-600 group-hover:translate-x-2 transition-transform">
                  {info.action}
                  <ChevronRight size={16} className="ml-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-900 text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">
              Prêt à Démarrer ?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Contactez-nous pour découvrir comment nos services peuvent répondre à vos besoins.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="px-8 py-4 bg-accent-300 hover:bg-accent-400 text-primary-900 font-medium rounded-xl transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Nous Contacter
                <ArrowRight size={20} className="ml-2 inline-block" />
              </Link>
              <Link 
                to="/products"
                className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all border border-white/30"
              >
                Voir Nos Produits
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;